"use client";

/**
 * Shared runtime font system — system fonts, Google Fonts, and CLOUD custom
 * fonts. Cloud fonts are .ttf/.otf/.woff/.woff2 files (e.g. downloaded from
 * dafont.com — check each font's licence before commercial use) uploaded by
 * an admin/creator to Firebase Storage and indexed in the `customFonts`
 * collection, so a font imported ONCE is available to everyone — in the
 * generative avatar customizer AND the Artwork Studio text tool.
 *
 * next/font can't be used here: families are chosen at runtime.
 */

import { db, storage } from "@/lib/firebase";
import { addDoc, collection, deleteDoc, doc, getDocs } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";

export const SYSTEM_FONT_NAMES = [
  "Impact", "Arial", "Georgia", "Helvetica Neue", "Times New Roman",
  "Courier New", "Verdana", "Trebuchet MS", "Comic Sans MS",
];

/** Display-leaning Google list for cover/name lettering pickers. */
export const DISPLAY_GOOGLE_FONTS = [
  "Anton", "Bebas Neue", "Archivo Black", "Oswald", "Bungee", "Bungee Shade",
  "Black Ops One", "Monoton", "Rubik Mono One", "Press Start 2P", "Orbitron",
  "Audiowide", "Righteous", "Bangers", "Permanent Marker", "Rock Salt",
  "Special Elite", "Abril Fatface", "Ultra", "Titan One", "Passion One",
  "Playfair Display", "UnifrakturMaguntia", "Pacifico", "Lobster",
  "Amatic SC", "Shadows Into Light", "Rampart One", "Fascinate Inline",
];

/** One family from the generated Google Fonts snapshot. */
export interface CatalogFont {
  family: string;
  category: FontCategory;
  weights: string[];
}

export type FontCategory = "sans" | "serif" | "display" | "script" | "mono";

export const FONT_CATEGORY_LABELS: Record<FontCategory, string> = {
  sans: "Sans-Serif & Modern",
  serif: "Serif & Luxury",
  display: "Bold Display & Impact",
  script: "Script & Calligraphy",
  mono: "Monospace & Cyber",
};

let catalogCache: CatalogFont[] | null = null;
let catalogPromise: Promise<CatalogFont[]> | null = null;

/**
 * The FULL Google Fonts library (~1900 families), generated into
 * public/font-catalog.json by scripts/gen-font-catalog.mjs.
 *
 * Fetched lazily and cached for the session — it is ~125 KB, far too much to
 * sit in the JS bundle, so pickers call this the first time they open.
 * Returns [] rather than throwing if the file is missing, so a picker degrades
 * to its curated list instead of breaking.
 */
export async function fetchFontCatalog(): Promise<CatalogFont[]> {
  if (catalogCache) return catalogCache;
  if (catalogPromise) return catalogPromise;
  catalogPromise = (async () => {
    try {
      const res = await fetch("/font-catalog.json");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      // Stored in a short-key shape to keep the file small.
      catalogCache = (data?.fonts ?? []).map((f: any) => ({
        family: f.f,
        category: (f.c || "sans") as FontCategory,
        weights: Array.isArray(f.w) && f.w.length ? f.w : ["400"],
      }));
      return catalogCache!;
    } catch (err) {
      console.error("[fetchFontCatalog]", err);
      catalogCache = [];
      return catalogCache;
    } finally {
      catalogPromise = null;
    }
  })();
  return catalogPromise;
}

export interface CloudFont {
  id: string;
  /** CSS family name (what you put in font-family). */
  family: string;
  url: string;
  format?: string;
  uploadedBy?: string;
  createdAt?: string;
}

const loadedGoogle = new Set<string>();
const registeredCloud = new Set<string>();
let cloudCache: CloudFont[] | null = null;
let cloudCacheAt = 0;

/** Inject a Google Fonts stylesheet for one family (idempotent). */
export function ensureGoogleFontLoaded(family: string) {
  if (typeof window === "undefined" || !family) return;
  if (SYSTEM_FONT_NAMES.includes(family) || registeredCloud.has(family) || loadedGoogle.has(family)) return;
  loadedGoogle.add(family);
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:wght@400;700&display=swap`;
  document.head.appendChild(link);
}

/**
 * Load MANY Google families in a single stylesheet request.
 *
 * A font picker showing the full library must render each option in its own
 * face, but one <link> per family would mean hundreds of requests. The css2
 * endpoint accepts repeated `family=` params, so a page of results costs one
 * request. Already-loaded families and cloud/system fonts are skipped.
 */
export function ensureGoogleFontsLoaded(families: string[]) {
  if (typeof window === "undefined") return;
  const pending = families.filter(f =>
    f && !SYSTEM_FONT_NAMES.includes(f) && !registeredCloud.has(f) && !loadedGoogle.has(f),
  );
  if (!pending.length) return;
  pending.forEach(f => loadedGoogle.add(f));

  // Keep URLs well under practical length limits — chunk generously.
  const CHUNK = 25;
  for (let i = 0; i < pending.length; i += CHUNK) {
    const families = pending.slice(i, i + CHUNK)
      .map(f => `family=${encodeURIComponent(f)}`)
      .join("&");
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?${families}&display=swap`;
    document.head.appendChild(link);
  }
}

/** Register one cloud font with the browser (idempotent). */
export async function registerCloudFont(f: CloudFont): Promise<void> {
  if (typeof window === "undefined" || registeredCloud.has(f.family)) return;
  try {
    const face = new FontFace(f.family, `url(${f.url})`);
    await face.load();
    (document as any).fonts?.add?.(face);
    registeredCloud.add(f.family);
  } catch (err) {
    console.error("[registerCloudFont]", f.family, err);
  }
}

/** Fetch the shared cloud font index (5-minute cache). */
export async function fetchCloudFonts(): Promise<CloudFont[]> {
  if (cloudCache && Date.now() - cloudCacheAt < 5 * 60_000) return cloudCache;
  try {
    const snap = await getDocs(collection(db, "customFonts"));
    cloudCache = snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<CloudFont, "id">) }))
      .filter(f => f.family && f.url);
    cloudCacheAt = Date.now();
    return cloudCache;
  } catch {
    return cloudCache || [];
  }
}

/** Fetch + register every cloud font; returns their family names. */
export async function loadAllCloudFonts(): Promise<string[]> {
  const fonts = await fetchCloudFonts();
  await Promise.allSettled(fonts.map(registerCloudFont));
  return fonts.map(f => f.family);
}

/** Load whatever kind of family this is (system → noop, cloud → FontFace,
 *  anything else → Google stylesheet). Safe to call speculatively. */
export async function ensureFontLoaded(family?: string | null) {
  if (!family || SYSTEM_FONT_NAMES.includes(family) || registeredCloud.has(family)) return;
  const cloud = (await fetchCloudFonts()).find(f => f.family === family);
  if (cloud) { await registerCloudFont(cloud); return; }
  ensureGoogleFontLoaded(family);
}

const FONT_EXT_RE = /\.(ttf|otf|woff2?)$/i;

/** Upload a font file to Storage + index it in customFonts (shared for all).
 *  Returns the registered CloudFont. Throws on invalid file type. */
export async function uploadCloudFont(file: File, uploadedBy?: string): Promise<CloudFont> {
  const m = file.name.match(FONT_EXT_RE);
  if (!m) throw new Error("Font must be .ttf, .otf, .woff or .woff2");
  const format = m[1].toLowerCase();
  const family = file.name.replace(FONT_EXT_RE, "").replace(/[_-]+/g, " ").trim() || `Custom ${Date.now()}`;
  const path = `fonts/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
  const r = ref(storage, path);
  await uploadBytes(r, file, { contentType: `font/${format === "ttf" ? "ttf" : format}` });
  const url = await getDownloadURL(r);
  const docRef = await addDoc(collection(db, "customFonts"), {
    family, url, format, uploadedBy: uploadedBy || null, createdAt: new Date().toISOString(),
  });
  const font: CloudFont = { id: docRef.id, family, url, format, uploadedBy };
  cloudCache = [...(cloudCache || []), font];
  await registerCloudFont(font);
  return font;
}

/** Font files worth extracting from an archive, best format first — a dafont
 *  zip often carries the same face as both .otf and .ttf. */
const ARCHIVE_FONT_RE = /\.(woff2|woff|otf|ttf)$/i;
/** Licence / readme text a font archive usually ships alongside the files. */
const ARCHIVE_TEXT_RE = /\.(txt|md)$/i;

export interface FontArchiveResult {
  fonts: CloudFont[];
  /** Readme / licence text found in the archive, so the terms can be shown
   *  before the font gets used commercially. */
  licence?: string;
  /** Font files that were in the archive but failed to upload. */
  failed: string[];
}

/**
 * Import a font ARCHIVE (.zip) — the format dafont.com and most free-font
 * sites actually hand you. Extracted in the browser; every font file inside is
 * uploaded to the shared library, and any readme/licence text is returned so
 * the terms can be read before the font is used.
 *
 * Many dafont faces are "free for PERSONAL use only". Uploads here are shared
 * platform-wide, which is commercial use — check the returned licence text.
 */
export async function extractFontsFromArchive(
  file: File,
): Promise<{ files: File[]; licence?: string }> {
  const { unzipSync, strFromU8 } = await import("fflate");

  const buf = new Uint8Array(await file.arrayBuffer());
  let entries: Record<string, Uint8Array>;
  try {
    entries = unzipSync(buf);
  } catch {
    throw new Error("That .zip couldn't be opened — it may be corrupt or password-protected.");
  }

  const names = Object.keys(entries).filter(n =>
    // Skip macOS resource-fork junk and directory entries.
    !n.startsWith("__MACOSX/") && !n.split("/").pop()?.startsWith("._") && !n.endsWith("/"),
  );

  const fontNames = names.filter(n => ARCHIVE_FONT_RE.test(n));
  if (!fontNames.length) {
    throw new Error("No .ttf, .otf, .woff or .woff2 file found inside that archive.");
  }

  // Surface the licence up front — this is the whole point of reading it.
  const licenceName = names.find(n => ARCHIVE_TEXT_RE.test(n));
  let licence: string | undefined;
  if (licenceName) {
    try { licence = strFromU8(entries[licenceName]).slice(0, 4000).trim() || undefined; } catch { /* ignore */ }
  }

  // One face may ship as both .otf and .ttf — keep the best format per name.
  const byFace = new Map<string, string>();
  const rank = (n: string) => ["woff2", "woff", "otf", "ttf"].indexOf(n.split(".").pop()!.toLowerCase());
  for (const n of fontNames) {
    const base = (n.split("/").pop() || n).replace(ARCHIVE_FONT_RE, "").toLowerCase();
    const existing = byFace.get(base);
    if (!existing || rank(n) < rank(existing)) byFace.set(base, n);
  }

  const files = [...byFace.values()].map(path => {
    const base = path.split("/").pop() || path;
    const ext = base.match(ARCHIVE_FONT_RE)![1].toLowerCase();
    // Copy into a fresh buffer — the unzip output is a view over a larger
    // ArrayBuffer, and uploading the view would send the whole archive.
    return new File([new Uint8Array(entries[path])], base, { type: `font/${ext}` });
  });

  return { files, licence };
}

export async function uploadFontArchive(file: File, uploadedBy?: string): Promise<FontArchiveResult> {
  const { files, licence } = await extractFontsFromArchive(file);

  const fonts: CloudFont[] = [];
  const failed: string[] = [];
  for (const f of files) {
    try {
      fonts.push(await uploadCloudFont(f, uploadedBy));
    } catch {
      failed.push(f.name);
    }
  }

  if (!fonts.length) throw new Error("Every font in that archive failed to upload.");
  return { fonts, licence, failed };
}

/** Upload a font file OR a .zip archive containing fonts. */
export async function importFontFile(file: File, uploadedBy?: string): Promise<FontArchiveResult> {
  if (/\.zip$/i.test(file.name) || file.type === "application/zip") {
    return uploadFontArchive(file, uploadedBy);
  }
  return { fonts: [await uploadCloudFont(file, uploadedBy)], failed: [] };
}

/** Remove a cloud font from the shared index (file stays in Storage). */
export async function deleteCloudFont(id: string): Promise<void> {
  await deleteDoc(doc(db, "customFonts", id));
  cloudCache = (cloudCache || []).filter(f => f.id !== id);
}
